import type { OnboardingStepId } from "./shell";
import type { ScreenId } from "./nav";
import type { SpecTab } from "./screens/SpecWorkspace";

export type CreateRouteStep = Exclude<OnboardingStepId, "specify">;

export type DocumentRouteTab = "output" | "evidence" | "activity";

export interface DocumentRouteQuery {
  tab?: DocumentRouteTab;
  ref?: string;
  q?: string;
  type: string[];
  discipline: string[];
  state: string[];
  outcome: string[];
  hit?: string;
}

export const emptyDocumentRouteQuery = (): DocumentRouteQuery => ({
  type: [],
  discipline: [],
  state: [],
  outcome: []
});

export type AppRoute =
  | { kind: "dashboard" }
  | { kind: "admin"; section: "llm" }
  | { kind: "create"; step: CreateRouteStep; projectId?: string | undefined }
  | { kind: "specify"; projectId: string }
  | { kind: "summary"; projectId: string }
  // The upload-first stepper's own URL (VIEWER-FLOW-CONTRACT-20260819.md /
  // B-RELOAD): distinct from every legacy route so a reload can find its way
  // back to NewTenderFlow without colliding with the old wizard's `/create/*`
  // semantics or the legacy Tender ingestion screen's `/tender` semantics.
  | { kind: "tenderFlow"; projectId: string }
  | {
      kind: "project";
      projectId: string;
      screen: Exclude<ScreenId, "spec" | "documents">;
    }
  | {
      kind: "documents";
      projectId: string;
      docId?: string;
      query: DocumentRouteQuery;
    }
  | {
      kind: "spec";
      projectId: string;
      apartmentTypeId: string;
      tab: SpecTab;
    }
  | { kind: "notFound"; attemptedPath: string; message: string };

export const projectScreenUrlSlugs = {
  overview: "overview",
  inputs: "inputs",
  tender: "tender",
  viewer: "viewer",
  requirements: "requirements",
  quote: "quote",
  compare: "compare",
  copilot: "copilot",
  agentTrace: "agent-trace",
  tokenUsage: "token-usage",
  decisions: "decisions",
  risks: "risks",
  scope: "scope",
  mapping: "mapping",
  export: "export"
} satisfies Record<Exclude<ScreenId, "spec" | "documents">, string>;

export const specTabUrlSlugs = {
  conn: "connections",
  points: "points-ports",
  prod: "products",
  floor: "floor-plan",
  panel: "panel-make-up",
  schedules: "schedules"
} satisfies Record<SpecTab, string>;

const projectScreenBySlug = invert(projectScreenUrlSlugs);
const specTabBySlug = invert(specTabUrlSlugs);
const createSteps = new Set<CreateRouteStep>([
  "start",
  "inputs",
  "requirements",
  "policy-flags"
]);

export function parseAppRoute(pathname: string, search = ""): AppRoute {
  const attemptedPath = pathname || "/";
  const segments = pathSegments(pathname);

  if (segments.length === 0 || (segments.length === 1 && segments[0] === "dashboard")) {
    return { kind: "dashboard" };
  }

  if (segments[0] === "admin") {
    return segments.length === 2 && segments[1] === "llm"
      ? { kind: "admin", section: "llm" }
      : unknownPath(attemptedPath);
  }

  if (segments[0] === "create") {
    if (segments.length > 2) {
      return unknownPath(attemptedPath);
    }

    const step = createStepFromSegment(segments[1] ?? "start");

    return step ? { kind: "create", step } : unknownPath(attemptedPath);
  }

  if (segments[0] !== "projects" || segments.length < 2) {
    return unknownPath(attemptedPath);
  }

  const projectId = decodeSegment(segments[1]);

  if (!projectId) {
    return unknownPath(attemptedPath);
  }

  const routeSlug = segments[2] ?? "overview";

  if (routeSlug === "summary" && segments.length === 3) {
    return { kind: "summary", projectId };
  }

  if (routeSlug === "specify" && segments.length === 3) {
    return { kind: "specify", projectId };
  }

  if (routeSlug === "upload" && segments.length === 3) {
    return { kind: "tenderFlow", projectId };
  }

  if (routeSlug === "create") {
    if (segments.length > 4) {
      return unknownPath(attemptedPath);
    }

    const step = createStepFromSegment(segments[3] ?? "start");

    return step ? { kind: "create", projectId, step } : unknownPath(attemptedPath);
  }

  if (routeSlug === "spec") {
    if (segments.length !== 5) {
      return unknownPath(attemptedPath);
    }

    const apartmentTypeId = decodeSegment(segments[3]);
    const tab = specTabBySlug[segments[4] ?? ""];

    return apartmentTypeId && tab
      ? { kind: "spec", projectId, apartmentTypeId, tab }
      : unknownPath(attemptedPath);
  }

  if (routeSlug === "documents") {
    if (segments.length !== 3 && segments.length !== 4) {
      return unknownPath(attemptedPath);
    }

    const docId = segments.length === 4 ? decodeSegment(segments[3]) : null;

    if (segments.length === 4 && !docId) {
      return unknownPath(attemptedPath);
    }

    return {
      kind: "documents",
      projectId,
      ...(docId ? { docId } : {}),
      query: parseDocumentRouteQuery(search)
    };
  }

  const screen = projectScreenBySlug[routeSlug];

  if (!screen || segments.length !== 3) {
    return unknownPath(attemptedPath);
  }

  return { kind: "project", projectId, screen };
}

export function routeToPath(route: Exclude<AppRoute, { kind: "notFound" }>): string {
  if (route.kind === "dashboard") {
    return "/dashboard";
  }

  if (route.kind === "admin") {
    return `/admin/${route.section}`;
  }

  if (route.kind === "create") {
    const suffix = route.projectId
      ? `/projects/${encodeSegment(route.projectId)}/create`
      : "/create";

    return `${suffix}/${route.step}`;
  }

  if (route.kind === "summary") {
    return `/projects/${encodeSegment(route.projectId)}/summary`;
  }

  if (route.kind === "specify") {
    return `/projects/${encodeSegment(route.projectId)}/specify`;
  }

  if (route.kind === "tenderFlow") {
    return `/projects/${encodeSegment(route.projectId)}/upload`;
  }

  if (route.kind === "spec") {
    return `/projects/${encodeSegment(route.projectId)}/spec/${encodeSegment(
      route.apartmentTypeId
    )}/${specTabUrlSlugs[route.tab]}`;
  }

  if (route.kind === "documents") {
    const path = `/projects/${encodeSegment(route.projectId)}/documents${
      route.docId ? `/${encodeSegment(route.docId)}` : ""
    }`;
    const query = serializeDocumentRouteQuery(route.query);

    return query ? `${path}?${query}` : path;
  }

  return `/projects/${encodeSegment(route.projectId)}/${projectScreenUrlSlugs[route.screen]}`;
}

export function routeKey(route: AppRoute): string {
  return route.kind === "notFound"
    ? `not-found:${route.attemptedPath}`
    : routeToPath(route);
}

function pathSegments(pathname: string): string[] {
  return pathname
    .split("/")
    .map((segment) => segment.trim())
    .filter(Boolean);
}

function createStepFromSegment(segment: string): CreateRouteStep | null {
  return createSteps.has(segment as CreateRouteStep)
    ? (segment as CreateRouteStep)
    : null;
}

function unknownPath(attemptedPath: string): AppRoute {
  return {
    kind: "notFound",
    attemptedPath,
    message: "That project link does not match a screen in Titan Specifier."
  };
}

function encodeSegment(value: string): string {
  return encodeURIComponent(value);
}

function decodeSegment(value: string | undefined): string | null {
  if (!value) {
    return null;
  }

  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

const documentTabs = new Set<DocumentRouteTab>(["output", "evidence", "activity"]);

function parseDocumentRouteQuery(search: string): DocumentRouteQuery {
  const params = new URLSearchParams(search);
  const tabValue = params.get("tab");
  const tab =
    tabValue && documentTabs.has(tabValue as DocumentRouteTab)
      ? (tabValue as DocumentRouteTab)
      : undefined;
  const query = emptyDocumentRouteQuery();
  const q = nonEmptyQueryValue(params.get("q"));
  const ref = nonEmptyQueryValue(params.get("ref"));
  const hit = nonEmptyQueryValue(params.get("hit"));

  if (tab && tab !== "output") {
    query.tab = tab;
  }
  if (q) {
    query.q = q;
  }
  if (ref) {
    query.ref = ref;
  }
  if (hit) {
    query.hit = hit;
  }

  query.type = sortedUniqueValues(params.getAll("type"));
  query.discipline = sortedUniqueValues(params.getAll("discipline"));
  query.state = sortedUniqueValues(params.getAll("state"));
  query.outcome = sortedUniqueValues(params.getAll("outcome"));

  return query;
}

function serializeDocumentRouteQuery(query: DocumentRouteQuery): string {
  const params = new URLSearchParams();

  if (query.tab && query.tab !== "output") {
    params.set("tab", query.tab);
  }
  if (query.ref?.trim()) {
    params.set("ref", query.ref.trim());
  }
  if (query.q?.trim()) {
    params.set("q", query.q.trim());
  }
  appendSortedQueryValues(params, "type", query.type);
  appendSortedQueryValues(params, "discipline", query.discipline);
  appendSortedQueryValues(params, "state", query.state);
  appendSortedQueryValues(params, "outcome", query.outcome);
  if (query.hit?.trim()) {
    params.set("hit", query.hit.trim());
  }

  return params.toString();
}

function appendSortedQueryValues(
  params: URLSearchParams,
  key: string,
  values: readonly string[]
): void {
  sortedUniqueValues(values).forEach((value) => params.append(key, value));
}

function sortedUniqueValues(values: readonly string[]): string[] {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))].sort(
    (left, right) => left.localeCompare(right)
  );
}

function nonEmptyQueryValue(value: string | null): string | undefined {
  const trimmed = value?.trim();

  return trimmed ? trimmed : undefined;
}

function invert<T extends Record<string, string>>(value: T): Record<string, keyof T> {
  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [entry, key])
  ) as Record<string, keyof T>;
}
